import { ReactNode, RefObject } from "react";
import LeafTileValue from "../../../common/leaf/LeafTileValue/LeafTileValue";
import "./IncentiveCalculation.scss";
import { IconMultiply } from "../../../common/icons/IconMultiply/IconMultiply";
import { IconEquals } from "../../../common/icons/IconEquals/IconEquals";
import { MULTIPLIER_DATA_TYPE } from "../../enums/requirements";
import { IconAdd } from "../../icons/IconAdd/IconAdd";

type IncentiveCalc = {
  title: string;
  amount: string;
  buttonAriaLabel?: string;
  icon?: ReactNode;
  hoverIcon?: ReactNode;
  iconAriaLabel?: string;
  symbolIcon?: ReactNode;
  callbackFn?: Function;
  ref?: RefObject<HTMLButtonElement | null>;
  hideDesktopIcon?: boolean;
  hideMobileIcon?: boolean;
  mobileInlineIcon?: boolean;
  type?: MULTIPLIER_DATA_TYPE;
};

type Props = {
  isLoading: boolean;
  base: IncentiveCalc;
  multiplier: IncentiveCalc;
  total: IncentiveCalc;
  isExpanded?: boolean;
  additionalAmount?: IncentiveCalc;
};

export const IncentiveCalculation = ({ isLoading, base, multiplier, total, isExpanded, additionalAmount }: Props) => {
  const incCalcClassName = additionalAmount ? " IncentiveCalculationKicker" : "";
  return (
    <div className={"IncentiveCalculation" + incCalcClassName}>
      <LeafTileValue
        id="multiplier-base"
        isLoading={isLoading}
        title={base.title}
        value={base.amount}
        icon={base?.icon ?? <></>}
        buttonAriaLabel={base?.buttonAriaLabel}
        callback={base?.callbackFn}
        hideDesktopIcon={base?.hideDesktopIcon}
        hideMobileIcon={base?.hideMobileIcon}
        mobileInlineIcon={base?.mobileInlineIcon}
      />
      <span className="IncentiveCalculation__symbol IncentiveCalculation__multiplierSymbol">
        {multiplier?.symbolIcon || <IconMultiply />}
      </span>
      <LeafTileValue
        id={
          isExpanded && multiplier.type === MULTIPLIER_DATA_TYPE.INCENTIVE
            ? "IncentiveCalculation-multiplier"
            : "incentive-calc-multiplier"
        }
        isLoading={isLoading}
        title={multiplier.title}
        value={multiplier.amount}
        icon={multiplier?.icon ?? <></>}
        buttonAriaLabel={multiplier?.iconAriaLabel}
        callback={multiplier?.callbackFn}
        symbolIcon={multiplier?.symbolIcon ?? <></>}
        buttonRef={multiplier.ref}
        hideDesktopIcon={multiplier?.hideDesktopIcon}
        hideMobileIcon={multiplier?.hideMobileIcon}
      />

      {additionalAmount && (
        <>
          <span className="IncentiveCalculation__symbol IncentiveCalculation__multiplierSymbol">{<IconAdd />}</span>
          <LeafTileValue
            id="incentive-calc-additionalAmount"
            isLoading={isLoading}
            title={additionalAmount.title}
            value={additionalAmount.amount}
            icon={additionalAmount?.icon ?? <></>}
            buttonAriaLabel={additionalAmount?.iconAriaLabel}
            callback={additionalAmount?.callbackFn}
            symbolIcon={additionalAmount?.symbolIcon ?? <></>}
            hideDesktopIcon={additionalAmount?.hideDesktopIcon}
            hideMobileIcon={additionalAmount?.hideMobileIcon}
          />
        </>
      )}
      <div className="IncentiveCalculation__divider" />
      <span className="IncentiveCalculation__symbol IncentiveCalculation__equalSymbol">
        <IconEquals />
      </span>
      <LeafTileValue
        id="incentive-calc-multiplier-total"
        isLoading={isLoading}
        title={total.title}
        value={total.amount}
        icon={total?.icon ?? <></>}
        buttonAriaLabel={total?.iconAriaLabel}
        callback={total?.callbackFn}
        symbolIcon={total?.symbolIcon ?? <></>}
        hideDesktopIcon={total?.hideDesktopIcon}
        hideMobileIcon={total?.hideMobileIcon}
      />
    </div>
  );
};
